import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AggregateDescriptor, groupBy, State } from "@progress/kendo-data-query";
import { plainToClass } from 'class-transformer';
import { BehaviorSubject, Observable } from "rxjs";
import { map, tap } from "rxjs/operators";
import { environment } from '../../../environments/environment';
import { Logging } from '../logging';
import { FieldDefinition } from '../models/form-builder/field-definition.model';
import { FormField } from '../models/form-builder/form-field.model';
import { FormInstanceElement } from '../models/form-builder/form-instance-element.model';
import { FormInstanceGridRow } from '../models/site-content/form-instance-grid-row.model';
import { FormInstance } from '../models/site-content/form-instance.model';
import { FormTemplateInstancesGridData } from '../models/site-content/form-template-instances-grid-data.model';
import { FormsGridRowOrderData } from '../models/site-content/forms-grid-row-order-data.model';
import { GroupedFormInstanceGridRowData } from '../models/site-content/grouped-form-instance-grid-row-data';
import { UserHomePagePermissions } from '../models/site-content/user-home-page-permissions.model';
import { FieldDefinitionService } from './field-definition.service';
import { ProgressIndicatorService } from './progress-indicator.service';
import { BrowserStorageService } from '../services/browserstorage.service';
import { MetadataFieldFactory } from '../models/form-template-instances/metadata-field-factory.model';

export class OrderFormsGridRowsData {
    public constructor(public formInstanceId: number, public formsGridOrder: number) { }
}
export interface MapOfFormsGridOrders {
    [formInstanceId: number]: number;
}

const CREATE_ACTION = "create";
const UPDATE_ACTION = "update";
const REMOVE_ACTION = "destroy";

// Question:  why is this class being defined when class FormInstanceService already exists?
//     Answer:  this service class is dedicated to communicating between a client Kendo grid
//              and a corresponding server service class.  Class FormInstanceService has a
//              broader set of responsibilities.

@Injectable({
    providedIn: 'root'
})
export class FormTemplateInstancesService extends BehaviorSubject<FormTemplateInstancesGridData> {
    // Properties.
    private readonly url = environment.apiUrl;
    private readonly endpoint: string = 'api/formTemplateInstances';
    private data: object[] = [];
    private gridData: FormTemplateInstancesGridData;
    private loading = false;

    // New:  save configuration data.
    private gridState: State = null;
    private columnDefinitions: FormField[] = null;

    // Hard wired aggregates (for now).
    /*
    private aggregates: AggregateDescriptor[] = [
        { field: "UnitPrice", aggregate: "sum" },
    ];
    */

    // Constructor.
    public constructor(
        private readonly http: HttpClient,
        progressIndicatorService: ProgressIndicatorService,
        private readonly fieldDefinitionService: FieldDefinitionService,
        private readonly browserStorageService: BrowserStorageService) {
        super(new FormTemplateInstancesGridData());
    }

    public formatResponse(data: FormInstance): FormInstance {
        let obj: FormInstance = FormInstance.loadFromDeserializedNetworkData(data);

        return (obj);
    }

    // Service methods.
    public read(
        formTemplateId: number,
        columnDefinitions: FormField[],
        selectedGridId: number,
        formFieldId: number,
        gridState: State,
        aggregatesParam: AggregateDescriptor[] = null,
        formInstanceId: number = -1,
        total: number = 0,
        overrideRequireFormGridFilterIfSet: boolean = false,
        filterByParentFolderIdOnly: boolean = false
    ): void {
        this.loading = true;
        this.gridState = gridState;

        // If we have grouping, request all records from the server.
        // Additionally, add any specified aggregates/aggregations.
        if ((this.gridState.group != null) && (this.gridState.group.length > 0)) {
            this.gridState.skip = 0;
            this.gridState.take = 1000000;
        }
        this.columnDefinitions = columnDefinitions;

        this.fetch(formTemplateId, columnDefinitions, selectedGridId, formFieldId, gridState, "", null, formInstanceId, total, overrideRequireFormGridFilterIfSet, filterByParentFolderIdOnly)
            .pipe(
                tap((data) => {
                    this.gridData = data;
                    this.loading = false;
                })
            )
            .subscribe((data) => {
                super.next(data);
            });
    }

    public getAll(formTemplateId: number, columnDefinitions: FormField[], formFieldId: number) {
        this.gridState = {
            skip: 0,
            take: 1000000, // effectively, "all"
            sort: [],
            filter: {
                filters: [],
                logic: 'and'
            }
        };
        const selectedGridId = 0; // selectedGridId is not relevant for this use case
        return this.fetch(formTemplateId, columnDefinitions, selectedGridId, formFieldId, this.gridState).toPromise();
    }

    public getFields(formTemplateId: number, gridFieldId: number = 0): Promise<FormField[]> {
        let baseUrl = this.url.endsWith('/') ? this.url : `${this.url}/`;
        let url = `${baseUrl}${this.endpoint}/GetFormFields/${formTemplateId}/${gridFieldId}`;

        return this.http.get<FormField[]>(url).toPromise().then(response => {
            return response;
        });
    }

    public resetItem(dataItem: object): void {
        if (!dataItem) {
            return;
        }

        // find orignal data item
        const originalDataItem = this.data.find(
            (item) => item['id'] === dataItem['id']
        );

        // revert changes
        Object.assign(originalDataItem, dataItem);

        super.next(this.gridData);
    }

    private reset() {
        this.data = [];
    }

    public get Loading() {
        return this.loading;
    }

    public setFormsGridOrderValues(formFieldId: number, mapOfFormsGridOrdersJson: string): Promise<boolean> {
        let baseUrl = this.url.endsWith('/') ? this.url : `${this.url}/`;
        let url = `${baseUrl}${this.endpoint}/SetFormsGridOrderValues/${formFieldId}`;

        let reorderData: FormsGridRowOrderData = new FormsGridRowOrderData(mapOfFormsGridOrdersJson);

        return this.http.post<boolean>(url, reorderData)
            .toPromise()
            .then(response => {
                return response;
            })
            .catch(this.handleError);
    }

    public getUserFormsGridPermissions(formFieldId: number): Promise<UserHomePagePermissions> {
        let url: string = `${this.url}/${this.endpoint}/getUserFormsGridPermissions/${formFieldId}`;

        return this.http.get<UserHomePagePermissions>(url)
            .toPromise()
            .then(response => {
                let result: UserHomePagePermissions = plainToClass(UserHomePagePermissions, response);
                return result;
            });
    }

    // Helper methods.
    private fetch(
        formTemplateId: number,
        columnDefinitions: FormField[],
        selectedGridId: number,
        formFieldId: number,
        gridState: State,
        action = "",
        data?: object[],
        formInstanceId: number = -1,
        total: number = 0,
        overrideRequireFormGridFilterIfSet: boolean = false,
        filterByParentFolderIdOnly: boolean = false
    ): Observable<FormTemplateInstancesGridData> {
        let baseUrl = this.url.endsWith('/') ? this.url : `${this.url}/`;
        let url = `${baseUrl}${this.endpoint}/Get/${formTemplateId}`;
        
        let payload = {
            configuredColumns: columnDefinitions,
            gridState: gridState,
            selectedGridId: selectedGridId,
            formFieldId: formFieldId,
            formInstanceId: formInstanceId,
            total: total,
            overrideRequireFormGridFilterIfSet: overrideRequireFormGridFilterIfSet,
            filterByParentFolderIdOnly: filterByParentFolderIdOnly
        }

        return <Observable<FormTemplateInstancesGridData>><unknown>this.http.post<FormTemplateInstancesGridData>(url, payload)
            .pipe(
                map((gridData) => {
                    if (gridData.total == 0) gridData.total = gridData.resultsFromServer?.length;
                    let gridDataObject = Object.assign(new FormTemplateInstancesGridData(), gridData); // converts to a fully-fledged FormTemplateInstancesGridData

                    if ((this.gridState.group == null) || (this.gridState.group.length == 0)) {
                        if (gridDataObject?.resultsFromServer?.length > 0) {
                            //***Get list of metadata fields
                            let metadataFieldNames: Array<string> = MetadataFieldFactory.getDummyMetaDataFormFieldNames();

                            for (let row of gridDataObject.resultsFromServer) {
                                let fiGridRow: FormInstanceGridRow = Object.assign(new FormInstanceGridRow(), row);
                                let formInstanceRow = FormInstanceGridRow.getSimpleFormsGridValues(fiGridRow, columnDefinitions);
                                formInstanceRow['FormOpenUrl'] = `site/${gridData.dataCollectionId}/form-instance/${fiGridRow.id}`;
                                formInstanceRow['isFavorite'] = row['favorite'];
                                if (this.columnDefinitions) {
                                    this.columnDefinitions.forEach(cd => {
                                        //***Iterate the non-metadata fields to assign the alias name/values
                                        //***Since metadata fields aren't db fields, they will never have an id > 0
                                        if (cd.id > 0) {
                                            //***Add a new column to the row when using aliases except when its a metadata field
                                            //***If statement prevents overwriting of metadata fields
                                            if (cd.alias != null && !metadataFieldNames.includes(cd.alias.toLowerCase()))
                                                formInstanceRow[cd.alias] = formInstanceRow[cd.name];
                                            //else if (cd.alias != null && cd.alias?.length > 0) {
                                                //***This cd is most likely using the same name as a metadata field so remove the alias to ignore it
                                                //cd.alias = undefined;
                                            //}
                                        }
                                    });
                                }
                                else {
                                    console.log('columnDefinitions missing');
                                }

                                gridDataObject.resultsAsPlainObjects.push(formInstanceRow);
                            }
                        }
                        return gridDataObject;
                    }
                    else {
                        // Note:  the server is now returning the grouped data and also the original, ungrouped data.
                        let groupedGridData: FormTemplateInstancesGridData = Object.assign(new FormTemplateInstancesGridData(), gridData);

                        let untypedGridData: any = gridData;
                        let serverResult: GroupedFormInstanceGridRowData = <GroupedFormInstanceGridRowData>untypedGridData;

                        let ungroupedData: object[] = [];

                        let results = serverResult.ungroupedRowData.resultsFromServer;
                        if ((results != null) && (results.length > 0)) {
                            for (let index: number = 0; index < results.length; index++) {
                                let row: FormInstanceGridRow = results[index];
                                let ungroupedRow: object = {};

                                for (let iCol: number = 0; iCol < this.columnDefinitions.length; iCol++) {
                                    let colDef: FormField = this.columnDefinitions[iCol];
                                    let fieldDef: FieldDefinition = this.fieldDefinitionService.getFieldDefinition(colDef.fieldDefinitionClassName);
                                    let fieldName: string = colDef.name.trim();
                                    let fieldValue: any = null;

                                    if ((row.rowData != null) && (row.rowData.length > 0)) {
                                        let colValue: FormInstanceElement = row.rowData.find(ff => (ff.formField.displayName == colDef.displayName) || (ff.formField.name == colDef.name));
                                        // 04-03-2024 note:  will enable the following line to replace the block below it when I have more time to test.
                                        fieldValue = fieldDef.customLogicHandler.unpackTemplateInstancesRow(colDef, colValue, row);
                                        if (fieldValue == null || fieldValue == "null") {
                                            fieldValue = '';
                                        }
                                    }

                                    ungroupedRow[fieldName] = fieldValue;
                                }

                                ungroupedData.push(ungroupedRow);
                            }
                        }

                        // Calculate the client-side group for comparison with the server-side group (debugging only).
                        //let clientGroupedDataForReference = groupBy(ungroupedData, this.gridState.group);

                        FormTemplateInstancesService.fixCaseOfAggregates(serverResult.groupedRowData, this.gridState.group[0].aggregates);
                        FormTemplateInstancesService.fixCaseOfFieldNames(serverResult.groupedRowData);

                        groupedGridData.resultsAsPlainObjects = serverResult.groupedRowData;

                        return groupedGridData;
                    }
                })
            );
    }

    private static fixCaseOfAggregates(groupedRowData: object[], aggregates: AggregateDescriptor[]): void {
        // What's the deal with this method?
        //
        // Answer:  as we generally do, capitialized property names in server data structures get serialized to the client as lowercase names,
        //          the casing only affecting the first letter of each property, not any embedded/internal capital letters.
        //
        //          This can affect aggregate names where aggregate values are indexed by a field's name which can start with a capital letter.
        //          For such aggregates, e.g. 'Count' or "Ounces per Serving", they will be returned by the server indexed by property names
        //          'count' and "ounces per Serving", respectively.  As a result of this, the Telerik/Kendo grid will not 'see' the aggregates.
        //          The following code compensates by adding an index for each original aggregate property name if the casing has been changed
        //          in the result coming from the server.  Using the 'Count' and "Ounces per Serving" example, whereas each hash/map of
        //          aggregates would be returned as { 'count': { COUNT_AGGREGATES }, 'ounces per Serving': { OUNCES_PER_SERVING_AGGREGATES }},
        //          this code will update such a hash to look like the following:
        //
        //              {
        //                'count': { COUNT_AGGREGATES }, 'Count': { COUNT_AGGREGATES },
        //                'ounces per Serving': { OUNCES_PER_SERVING_AGGREGATES }, 'Ounces per Serving': { OUNCES_PER_SERVING_AGGREGATES }
        //              }
        if ((groupedRowData != null) && (groupedRowData.length > 0) && (aggregates != null) && (aggregates.length > 0)) {
            for (let index: number = 0; index < groupedRowData.length; index++) {
                let groupedRow: object = groupedRowData[index];
                let rowAggregates = groupedRow['aggregates'];
                if (rowAggregates != null) {
                    let keys: string[] = Object.keys(rowAggregates);
                    for (let keyIndex: number = 0; keyIndex < keys.length; keyIndex++) {
                        let key: string = keys[keyIndex];

                        let matchingAggregate: AggregateDescriptor = aggregates.find(ag => ag.field.toLowerCase() == key.toLowerCase());
                        if ((matchingAggregate != null) && (matchingAggregate.field != key)) {
                            let fieldAggregates = rowAggregates[key];
                            rowAggregates[matchingAggregate.field] = fieldAggregates;
                        }
                    }
                }

                let items: object[] = groupedRow['items'];
                if ((items != null) && (items.length > 0)) {
                    // Fix any case problems with the subgrouped rows.
                    FormTemplateInstancesService.fixCaseOfAggregates(items, aggregates);
                }
            }
        }
    }

    private static fixCaseOfFieldNames(groupedRowData: object[]): void {
        // What's the deal with this method?
        //
        // Answer:  Similar to grouping aggregates, item property names are returned with
        //          lowercase initial letter, which may not match the field name:
        //
        // "groupedRowData": [
        //   {
        //    "field": "WorkflowState",             <-- capitalized field name
        //    "value": "Start",
        //    "items": [
        //        {
        //           "favorite": false,
        //           "subscription": 0,
        //           "id": 4535,
        //           "name": "Destination",
        //           "folderName": "Travel Plans",
        //           "workflowState": "Start",      <-- mismatched field name
        //           "integer0": 22034,
        //           "shortText0": "Baltimore",
        //       },

        if ((groupedRowData != null) && (groupedRowData.length > 0)) {
            //iterate through grouped rows
            for (let index: number = 0; index < groupedRowData.length; index++) {
                let groupedRow: object = groupedRowData[index];
                let rowItems = groupedRow['items'];
                if (rowItems != null) {
                    //iterate through items in this group
                    for (let itemIndex: number = 0; itemIndex < rowItems.length; itemIndex++) {
                        let keys: string[] = Object.keys(rowItems[itemIndex]);
                        //iterate through properties of this item
                        for (let keyIndex: number = 0; keyIndex < keys.length; keyIndex++) {
                            let itemKey = keys[keyIndex];
                            let fixedKey = itemKey[0].toUpperCase() + itemKey.substring(1);
                            //if keys mismatch, add capitalized property with same value
                            if (itemKey != fixedKey) {
                                rowItems[itemIndex][fixedKey] = rowItems[itemIndex][itemKey];
                            }
                        }
                    }
                }

                let items: object[] = groupedRow['items'];
                if ((items != null) && (items.length > 0)) {
                    // Fix child groupings by recursion
                    FormTemplateInstancesService.fixCaseOfFieldNames(items);
                }
            }
        }
    }

    private handleError(error: Response | any): Promise<any> {
        Logging.log(error);
        return Promise.reject(error.message || error);
    }

    /*
    private static addExampleAggregatesToGroup(groupDescriptor: GroupDescriptor): void {
        let aggregates: AggregateDescriptor[] =
            [
                { field: 'Count', aggregate: "sum" },
                { field: 'Count', aggregate: "count" },
                //{ field: 'Count', aggregate: "average" },
                //{ field: 'Count', aggregate: "min" },
                //{ field: 'Count', aggregate: "max" },

                { field: 'Ounces per Serving', aggregate: "sum" },
                { field: 'Ounces per Serving', aggregate: "count" },
                //{ field: 'Ounces per Serving', aggregate: "average" },
                //{ field: 'Ounces per Serving', aggregate: "min" },
                //{ field: 'Ounces per Serving', aggregate: "max" },
            ];
        //this.gridState.group.map((group) => (group.aggregates = this.aggregates));
        groupDescriptor.aggregates = aggregates;
    }
    */
}
